// Firestore service for the "What's New" announcement system (D-14 to D-17).
//
// subscribeToAnnouncements is the live listener that lets an already-open
// session see a newly published announcement without passing through the
// sign-in page (D-15). markAnnouncementsSeen writes to users/{uid}, which is
// NOT workspace-scoped, so it uses the documented unguardedWrites escape
// hatch (src/services/firestoreWrites.js) — a suspended workspace must not
// block a user from dismissing a pop-up.

import { collection, doc, getDoc, onSnapshot, arrayUnion } from 'firebase/firestore';
import { unguardedWrites } from './firestoreWrites';
import { db } from '../firebase';
import {
    ANNOUNCEMENTS_COLLECTION,
    SEEN_ANNOUNCEMENTS_FIELD,
    MIGRATION_REPORTS_SUBCOLLECTION,
    normalizeAnnouncement,
} from '../utils/announcements';

/**
 * Opens a live listener on the announcements collection. Deliberately has no
 * query predicate or server-side sort applied — sorting happens client-side
 * via sortNewestFirst (src/utils/announcements.js), per the
 * checkOrderByFields and missing-field-silently-drops-the-doc lessons this
 * codebase has already paid for elsewhere. Docs are mapped through
 * normalizeAnnouncement and nulls (malformed docs) are dropped before onNext
 * is called.
 *
 * @param {(announcements: Array<object>) => void} onNext
 * @param {(error: Error) => void} [onError]
 * @returns {Function} unsubscribe
 */
export function subscribeToAnnouncements(onNext, onError = () => {}) {
    const ref = collection(db, ANNOUNCEMENTS_COLLECTION);
    return onSnapshot(
        ref,
        (snapshot) => {
            const announcements = snapshot.docs
                .map((docSnap) => normalizeAnnouncement(docSnap.id, docSnap.data()))
                .filter(Boolean);
            onNext(announcements);
        },
        onError
    );
}

/**
 * Reads the caller's own seen-announcement ids off users/{uid}. Returns an
 * empty array when the user doc or the field is absent, never throws.
 *
 * @param {string} uid
 * @returns {Promise<Array<string>>}
 */
export async function fetchSeenAnnouncementIds(uid) {
    if (!uid) return [];
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return [];
    const ids = snap.data()?.[SEEN_ANNOUNCEMENTS_FIELD];
    return Array.isArray(ids) ? ids : [];
}

/**
 * Marks one or more announcements seen for a user. No-ops on an empty id
 * list. User-scoped, not workspace-scoped, so this goes through the
 * documented unguardedWrites escape hatch rather than the workspace
 * read-only chokepoint.
 *
 * @param {string} uid
 * @param {Array<string>} ids
 * @returns {Promise<void>}
 */
export async function markAnnouncementsSeen(uid, ids) {
    if (!uid || !Array.isArray(ids) || ids.length === 0) return;
    await unguardedWrites.setDoc(
        doc(db, 'users', uid),
        { [SEEN_ANNOUNCEMENTS_FIELD]: arrayUnion(...ids) },
        { merge: true }
    );
}

/**
 * Fetches a per-workspace migration report doc (D-17). Never throws into
 * the app shell — on any error it logs and returns null, which callers
 * treat the same as "this workspace was never touched" (isVisibleToWorkspace
 * in src/utils/announcements.js already handles a falsy report).
 *
 * @param {string} workspaceId
 * @param {string} reportId
 * @returns {Promise<object|null>}
 */
export async function fetchMigrationReport(workspaceId, reportId) {
    if (!workspaceId || !reportId) return null;
    try {
        const snap = await getDoc(doc(db, 'workspaces', workspaceId, MIGRATION_REPORTS_SUBCOLLECTION, reportId));
        return snap.exists() ? snap.data() : null;
    } catch (error) {
        console.error('fetchMigrationReport failed:', error);
        return null;
    }
}

export default {
    subscribeToAnnouncements,
    fetchSeenAnnouncementIds,
    markAnnouncementsSeen,
    fetchMigrationReport,
};
